import { ReactElement, useEffect } from 'react'
import { ROOT_DECISION_NODE_ID, SystemPolicyId } from '@vms/vmspro3-core/dist/systemConsts'
import { Button, Divider, Spin } from 'antd'

import { Page } from '../../common/Page'
import { DecisionFolderChildren } from './DecisionFolderChildren'

import { useQuerystringValue } from '../../../hooks/useQuerystringValue'
import { useAccount } from '../../../context'
import { useFetchUsers } from '../../../redux/hooks'
import { useDecisionFolder2, type UseDecisionFolder2Result } from '../../../hooks/useDecisionFolder2'
import SettingOutlined from '@ant-design/icons/SettingOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import { NavLink } from 'react-router-dom'
import FolderPolicyTable from '../../common/FolderPolicyTable'

function getBreadcrumbRoutes(data: UseDecisionFolder2Result, accountCommonId: string) {
  return [
    { path: `/${accountCommonId}`, breadcrumbName: 'Home' },
    ...data.ancestors.map(d => ({ path: `/${accountCommonId}/decision?folder=${d.id}`, breadcrumbName: d.name })),
    { path: `/${accountCommonId}/decision?folder=${data.folder.id}`, breadcrumbName: data.folder.name },
  ]
}

interface DecisionExplorerProps {
  accountCommonId: string
  accountId: string
  decisionFolderId: string
}
function DecisionExplorer({ accountCommonId, accountId, decisionFolderId }: DecisionExplorerProps): ReactElement {
  const { data: directoryData, loading, refetch } = useDecisionFolder2({
    decisionFolderId,
    accountId,
  })
  const folder = directoryData?.folder
  const { accountUserRecord } = useAccount()

  const [view, setView] = useQuerystringValue('view', '', false)

  useEffect(() => {
    if (directoryData) {
      const childAncestry = [...directoryData.ancestors.map(a => a.id), directoryData.folder.id].join('/')
      console.log(`folder path: /${childAncestry}`)
    }
  }, [folder, directoryData])

  if (loading || !directoryData) return <Spin />

  const breadcrumbRoutes = getBreadcrumbRoutes(directoryData, accountCommonId)

  const extra =
    view === 'config' ? (
      <Button icon={<CheckOutlined />} type="primary" onClick={() => setView('')}>
        Done
      </Button>
    ) : accountUserRecord.policyIds.includes(SystemPolicyId.DECISION_ADMIN) ? (
      <Button icon={<SettingOutlined />} onClick={() => setView('config')}>
        Configuration
      </Button>
    ) : undefined

  return view === 'config' ? (
    <Page loading={loading} title={folder?.name} breadcrumbRoutes={breadcrumbRoutes} extra={extra}>
      <DecisionFolderConfig decisionFolderId={decisionFolderId} />
    </Page>
  ) : (
    <Page loading={loading} title={folder?.name} breadcrumbRoutes={breadcrumbRoutes} extra={extra}>
      <DecisionFolderChildren
        accountCommonId={accountCommonId}
        decisionFolderId={decisionFolderId}
        directoryData={directoryData}
        refetch={refetch}
      />
    </Page>
  )
}

export function DecisionFolderPage(): ReactElement {
  const [decisionFolderId] = useQuerystringValue('folder', ROOT_DECISION_NODE_ID)
  const { accountId, accountCommonId } = useAccount()

  return (
    <DecisionExplorer
      accountCommonId={accountCommonId}
      accountId={accountId}
      decisionFolderId={decisionFolderId}
    />
  )
}

type DecisionFolderConfigProps = {
  decisionFolderId: string
}
function DecisionFolderConfig(props: DecisionFolderConfigProps) {
  useFetchUsers()
  const { accountCommonId } = useAccount()
  return (
    <div>
      <h2>Decision Users</h2>
      <p>
        Only users with the role "Decision Users" are listed here; go to{' '}
        <NavLink to={`/${accountCommonId}/roles`}>Admin &gt; Roles</NavLink> to add users to this role.
      </p>
      <p>
        Access specified here will override access from ancestor folders, and applies to descendant folders (but
        can be overridden by descendant folders). Set access to "Inherit" to inherit access level from nearest
        ancestor folder.
      </p>
      <Divider orientationMargin={16} />
      <FolderPolicyTable folderId={props.decisionFolderId} />
    </div>
  )
}
